import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { styled } from 'styled-components';
import {
  useGetIsShopperTyping,
  useSetAllConversationMessagesAsRead,
} from '@vs-online-only-app/state';
import { usePrevious } from '@vs-online-only-app/ui';
import SelectedConversationMessagesListItem from './SelectedConversationMessagesListItem';
import useSelectedConversationPaginatedMessages from '../../hooks/useSelectedConversationPaginatedMessages';
import TypingMessage from '../message/TypingMessage';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';

const StyledStack = styled(Stack)`
  overflow: auto;
  height: 100%;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(2)}`};
  margin: ${({ theme }) => `0 -${theme.spacing(1)}`};
`;

const SelectedConversationMessageList: FC = () => {
  const selectedConversation = useSelectedHeroConversation();
  const { data: isShopperTyping } = useGetIsShopperTyping(
    selectedConversation?.twilioConversationId,
  );

  const { allMessages, pages, fetchNextPage } =
    useSelectedConversationPaginatedMessages();

  const messagesListRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState<number>(0);

  const pageCount = useMemo(() => (pages ? pages.length : 0), [pages]);
  const prevMessagePageCount = usePrevious(pageCount);
  const setAllConversationMessagesAsRead = useSetAllConversationMessagesAsRead(
    selectedConversation?.twilioConversationId,
  );

  const latestTwilioMessage = allMessages[allMessages.length - 1]?.index
    ? allMessages[allMessages.length - 1]
    : undefined;

  useEffect(() => {
    if (
      latestTwilioMessage &&
      selectedConversation &&
      !selectedConversation.archiveAttributes
    ) {
      setAllConversationMessagesAsRead.mutateAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestTwilioMessage, selectedConversation]);

  useEffect(() => {
    if (prevMessagePageCount && prevMessagePageCount !== pageCount) {
      messagesListRef.current?.scrollTo({
        top: messagesListRef.current.scrollHeight - scrollHeight,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount, prevMessagePageCount]);

  useEffect(() => {
    setTimeout(
      () =>
        messagesListRef.current?.scrollTo({
          top: messagesListRef.current.scrollHeight,
        }),
      500,
    );
  }, [latestTwilioMessage, isShopperTyping]);

  useEffect(() => {
    const messageList = messagesListRef.current;

    const handleScroll = () => {
      if (messageList?.scrollTop === 0) {
        setScrollHeight(messageList.scrollHeight);
        fetchNextPage();
      }
    };
    messageList?.addEventListener('scroll', handleScroll);
    return () => messageList?.removeEventListener('scroll', handleScroll);
  }, [allMessages, fetchNextPage]);

  return (
    <StyledStack ref={messagesListRef}>
      {allMessages.map((message) => (
        <SelectedConversationMessagesListItem
          message={message}
          key={message.sid}
        />
      ))}
      {isShopperTyping && <TypingMessage />}
    </StyledStack>
  );
};

export default memo(SelectedConversationMessageList);
